import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";

function AdminRozNamachaFields() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Default column definitions (used for reset)
  const defaultSupplierColumns = [
    { key: "date", labelEn: "Date", labelUr: "تاریخ", type: "date", isDefault: true },
    { key: "billNumber", labelEn: "Bill Number", labelUr: "بل نمبر", type: "text", isDefault: true },
    { key: "supplierName", labelEn: "Supplier", labelUr: "سپلائر", type: "text", isDefault: true },
    { key: "supplierPhone", labelEn: "Phone", labelUr: "فون", type: "text", isDefault: true },
    { key: "totalAmount", labelEn: "Total Amount", labelUr: "کل رقم", type: "number", isDefault: true },
    { key: "paidAmount", labelEn: "Paid", labelUr: "ادا شده", type: "number", isDefault: true },
    { key: "remainingAmount", labelEn: "Remaining", labelUr: "باقی", type: "number", isDefault: true },
    { key: "status", labelEn: "Status", labelUr: "حالت", type: "text", isDefault: true },
  ];

  const defaultCustomerColumns = [
    { key: "date", labelEn: "Date", labelUr: "تاریخ", type: "date", isDefault: true },
    { key: "receiptNumber", labelEn: "Receipt Number", labelUr: "رسید نمبر", type: "text", isDefault: true },
    { key: "customerName", labelEn: "Customer", labelUr: "کسٹمر", type: "text", isDefault: true },
    { key: "customerPhone", labelEn: "Phone", labelUr: "فون", type: "text", isDefault: true },
    { key: "totalAmount", labelEn: "Total Amount", labelUr: "کل رقم", type: "number", isDefault: true },
    { key: "paidAmount", labelEn: "Paid", labelUr: "ادا شده", type: "number", isDefault: true },
    { key: "remainingAmount", labelEn: "Remaining", labelUr: "باقی", type: "number", isDefault: true },
    { key: "status", labelEn: "Status", labelUr: "حالت", type: "text", isDefault: true },
  ];

  const defaultRecoveryColumns = [
    { key: "date", labelEn: "Date", labelUr: "تاریخ", type: "date", isDefault: true },
    { key: "recoveryNumber", labelEn: "Recovery Number", labelUr: "ریکوری نمبر", type: "text", isDefault: true },
    { key: "partyName", labelEn: "Party", labelUr: "پارٹی", type: "text", isDefault: true },
    { key: "partyPhone", labelEn: "Phone", labelUr: "فون", type: "text", isDefault: true },
    { key: "amount", labelEn: "Amount", labelUr: "رقم", type: "number", isDefault: true },
    { key: "description", labelEn: "Description", labelUr: "تفصیل", type: "text", isDefault: true },
    { key: "status", labelEn: "Status", labelUr: "حالت", type: "text", isDefault: true },
  ];

  const defaultExpenseColumns = [
    { key: "date", labelEn: "Date", labelUr: "تاریخ", type: "date", isDefault: true },
    { key: "expenseNumber", labelEn: "Expense Number", labelUr: "خرچ نمبر", type: "text", isDefault: true },
    { key: "category", labelEn: "Category", labelUr: "زمرہ", type: "text", isDefault: true },
    { key: "description", labelEn: "Description", labelUr: "تفصیل", type: "text", isDefault: true },
    { key: "amount", labelEn: "Amount", labelUr: "رقم", type: "number", isDefault: true },
    { key: "paymentMethod", labelEn: "Payment Method", labelUr: "ادائیگی کا طریقہ", type: "text", isDefault: true },
    { key: "status", labelEn: "Status", labelUr: "حالت", type: "text", isDefault: true },
  ];

  // Supplier, customer, recovery & expense column definitions
  const [supplierColumns, setSupplierColumns] = useState([...defaultSupplierColumns]);
  const [customerColumns, setCustomerColumns] = useState([...defaultCustomerColumns]);
  const [recoveryColumns, setRecoveryColumns] = useState([...defaultRecoveryColumns]);
  const [expenseColumns, setExpenseColumns] = useState([...defaultExpenseColumns]);
  const [editingField, setEditingField] = useState(null); // Track which field is being edited: "supplier_0", "customer_1", "recovery_2", "expense_3"

  const getToken = () => localStorage.getItem("adminToken");

  useEffect(() => {
    const token = getToken();
    if (!token) {
      toast.error("Please login as admin first");
      navigate("/admin/login");
      return;
    }
    fetchCompanies();
  }, [navigate]);

  // Track if we're loading to prevent auto-save during load
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  // Auto-save disabled - only manual save buttons will save data
  // useEffect(() => {
  //   if (!selectedCompany || isLoadingTemplate) return;
  //   
  //   const timeoutId = setTimeout(() => {
  //     try {
  //       const storageKey = `rozTemplate_${selectedCompany._id}`;
  //       const dataToSave = JSON.stringify({ supplierColumns, customerColumns });
  //       localStorage.setItem(storageKey, dataToSave);
  //       console.log("Auto-saved columns to localStorage:", storageKey);
  //     } catch (error) {
  //       console.error("Auto-save error:", error);
  //     }
  //   }, 1000);
  //   return () => clearTimeout(timeoutId);
  // }, [supplierColumns, customerColumns, selectedCompany, isLoadingTemplate]);

  const fetchCompanies = async () => {
    try {
      const token = getToken();
      const response = await api.get("/admin/companies", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("Fetch companies error:", error);
      toast.error("Failed to load companies");
    }
  };

  const handleCompanySelect = async (companyId) => {
    if (!companyId) {
      setSelectedCompany(null);
      setSupplierColumns([...defaultSupplierColumns]);
      setCustomerColumns([...defaultCustomerColumns]);
      setRecoveryColumns([...defaultRecoveryColumns]);
      setExpenseColumns([...defaultExpenseColumns]);
      setIsLoadingTemplate(false);
      return;
    }
    const company = companies.find(c => c._id === companyId);
    if (!company) {
      console.error("Company not found:", companyId);
      return;
    }
    
    // Prevent auto-save during load
    setIsLoadingTemplate(true);
    
    // Set company first
    setSelectedCompany(company);

    // Try to load saved template from localStorage (per-company)
    try {
      const storageKey = `rozTemplate_${companyId}`;
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const tpl = JSON.parse(raw);
        console.log("Loading saved template from localStorage:", storageKey, tpl);
        
        // Always load saved columns if they exist, even if empty
        if (tpl.supplierColumns && Array.isArray(tpl.supplierColumns)) {
          console.log("Loading supplierColumns:", tpl.supplierColumns.length, "columns", tpl.supplierColumns);
          setSupplierColumns(tpl.supplierColumns);
        } else {
          console.log("No supplierColumns in saved template, using defaults");
          setSupplierColumns([...defaultSupplierColumns]);
        }
        
        if (tpl.customerColumns && Array.isArray(tpl.customerColumns)) {
          console.log("Loading customerColumns:", tpl.customerColumns.length, "columns", tpl.customerColumns);
          setCustomerColumns(tpl.customerColumns);
        } else {
          console.log("No customerColumns in saved template, using defaults");
          setCustomerColumns([...defaultCustomerColumns]);
        }

        if (tpl.recoveryColumns && Array.isArray(tpl.recoveryColumns)) {
          console.log("Loading recoveryColumns:", tpl.recoveryColumns.length, "columns", tpl.recoveryColumns);
          setRecoveryColumns(tpl.recoveryColumns);
        } else {
          console.log("No recoveryColumns in saved template, using defaults");
          setRecoveryColumns([...defaultRecoveryColumns]);
        }

        if (tpl.expenseColumns && Array.isArray(tpl.expenseColumns)) {
          console.log("Loading expenseColumns:", tpl.expenseColumns.length, "columns", tpl.expenseColumns);
          setExpenseColumns(tpl.expenseColumns);
        } else {
          console.log("No expenseColumns in saved template, using defaults");
          setExpenseColumns([...defaultExpenseColumns]);
        }
      } else {
        // No saved template, use defaults
        console.log("No saved template found for company:", companyId, "using defaults");
        setSupplierColumns([...defaultSupplierColumns]);
        setCustomerColumns([...defaultCustomerColumns]);
        setRecoveryColumns([...defaultRecoveryColumns]);
        setExpenseColumns([...defaultExpenseColumns]);
      }
    } catch (error) {
      console.error("Error loading template from localStorage:", error);
      // On error, use defaults
      setSupplierColumns([...defaultSupplierColumns]);
      setCustomerColumns([...defaultCustomerColumns]);
      setRecoveryColumns([...defaultRecoveryColumns]);
      setExpenseColumns([...defaultExpenseColumns]);
    } finally {
      // Re-enable auto-save after a short delay
      setTimeout(() => {
        setIsLoadingTemplate(false);
      }, 500);
    }
  };

  const handleAddColumn = (type) => {
    const newCol = {
      key: "",
      labelEn: "",
      labelUr: "",
      type: "text",
      isDefault: false
    };
    if (type === "supplier") {
      setSupplierColumns(prev => [...prev, newCol]);
    } else if (type === "customer") {
      setCustomerColumns(prev => [...prev, newCol]);
    } else if (type === "recovery") {
      setRecoveryColumns(prev => [...prev, newCol]);
    } else if (type === "expense") {
      setExpenseColumns(prev => [...prev, newCol]);
    }
  };

  const handleInsertColumn = (type, index) => {
    let updater, current;
    if (type === "supplier") {
      updater = setSupplierColumns;
      current = supplierColumns;
    } else if (type === "customer") {
      updater = setCustomerColumns;
      current = customerColumns;
    } else if (type === "recovery") {
      updater = setRecoveryColumns;
      current = recoveryColumns;
    } else if (type === "expense") {
      updater = setExpenseColumns;
      current = expenseColumns;
    } else {
      return;
    }
    
    const newCol = {
      key: "",
      labelEn: "",
      labelUr: "",
      type: "text",
      isDefault: false
    };
    const updated = [...current];
    updated.splice(index + 1, 0, newCol);
    updater(updated);
  };

  const handleUpdateColumn = (type, index, field, value) => {
    let updater, current;
    if (type === "supplier") {
      updater = setSupplierColumns;
      current = supplierColumns;
    } else if (type === "customer") {
      updater = setCustomerColumns;
      current = customerColumns;
    } else if (type === "recovery") {
      updater = setRecoveryColumns;
      current = recoveryColumns;
    } else if (type === "expense") {
      updater = setExpenseColumns;
      current = expenseColumns;
    } else {
      return;
    }
    
    const updated = [...current];
    updated[index] = { ...updated[index], [field]: value };
    updater(updated);
    // Auto-save is handled by useEffect hook
  };

  const handleRemoveColumn = (type, index) => {
    let updater, current;
    if (type === "supplier") {
      updater = setSupplierColumns;
      current = supplierColumns;
    } else if (type === "customer") {
      updater = setCustomerColumns;
      current = customerColumns;
    } else if (type === "recovery") {
      updater = setRecoveryColumns;
      current = recoveryColumns;
    } else if (type === "expense") {
      updater = setExpenseColumns;
      current = expenseColumns;
    } else {
      return;
    }
    
    const col = current[index];

    // Confirmation dialog (applies to all fields, default بھی)
    const confirmDelete = window.confirm(
      `کیا آپ واقعی یہ فیلڈ delete کرنا چاہتے ہیں؟\n\nField: ${col.labelEn || col.labelUr || col.key}\n\nیہ عمل واپس نہیں ہو سکے گا۔`
    );
    
    if (!confirmDelete) return;

    const updatedColumns = current.filter((_, i) => i !== index);
    updater(updatedColumns);
    setEditingField(null); // Clear editing state
    
    // Auto-save after deletion
    if (selectedCompany) {
      setTimeout(() => {
        try {
          const storageKey = `rozTemplate_${selectedCompany._id}`;
          let dataToSave;
          if (type === "supplier") {
            dataToSave = JSON.stringify({ supplierColumns: updatedColumns, customerColumns, recoveryColumns, expenseColumns });
          } else if (type === "customer") {
            dataToSave = JSON.stringify({ supplierColumns, customerColumns: updatedColumns, recoveryColumns, expenseColumns });
          } else if (type === "recovery") {
            dataToSave = JSON.stringify({ supplierColumns, customerColumns, recoveryColumns: updatedColumns, expenseColumns });
          } else if (type === "expense") {
            dataToSave = JSON.stringify({ supplierColumns, customerColumns, recoveryColumns, expenseColumns: updatedColumns });
          }
          
          localStorage.setItem(storageKey, dataToSave);
          toast.success("Field deleted and saved!");
        } catch (error) {
          console.error("Auto-save after delete error:", error);
        }
      }, 100);
    }
  };

  const handleEditField = (type, index) => {
    const fieldKey = `${type}_${index}`;
    if (editingField === fieldKey) {
      // Save and exit edit mode
      handleSaveSingleField(type, index);
      setEditingField(null);
    } else {
      // Enter edit mode
      setEditingField(fieldKey);
    }
  };

  const handleSaveSingleField = (type, index) => {
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }
    
    // Force immediate permanent save
    try {
      const storageKey = `rozTemplate_${selectedCompany._id}`;
      const dataToSave = JSON.stringify({ supplierColumns, customerColumns, recoveryColumns, expenseColumns });
      localStorage.setItem(storageKey, dataToSave);
      
      // Verify it was saved permanently
      const verify = localStorage.getItem(storageKey);
      if (verify) {
        const parsed = JSON.parse(verify);
        console.log("Permanently saved to localStorage:", storageKey, parsed);
        toast.success(`Field permanently saved!`);
      } else {
        toast.error("Failed to save - localStorage write failed");
      }
    } catch (error) {
      console.error("Save single field error:", error);
      toast.error("Failed to save field: " + error.message);
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }
    try {
      setIsLoading(true);
      const storageKey = `rozTemplate_${selectedCompany._id}`;
      const dataToSave = JSON.stringify({ supplierColumns, customerColumns, recoveryColumns, expenseColumns });
      
      // Permanent save to localStorage
      localStorage.setItem(storageKey, dataToSave);
      
      // Verify it was saved permanently
      const verify = localStorage.getItem(storageKey);
      if (verify && verify === dataToSave) {
        const parsed = JSON.parse(verify);
        toast.success("All fields permanently saved!");
        console.log("Permanently saved all fields to localStorage:", storageKey, {
          supplierCount: parsed.supplierColumns?.length || 0,
          customerCount: parsed.customerColumns?.length || 0,
          recoveryCount: parsed.recoveryColumns?.length || 0,
          expenseCount: parsed.expenseColumns?.length || 0,
          data: parsed
        });
      } else {
        toast.error("Failed to save - localStorage write failed");
        console.error("localStorage verification failed");
      }

      // Backend persistence - Uncomment when backend route is ready
      // const token = getToken();
      // try {
      //   await api.post("/admin/roznamacha/template", {
      //     companyId: selectedCompany._id,
      //     supplierColumns,
      //     customerColumns,
      //     recoveryColumns,
      //     expenseColumns
      //   }, {
      //     headers: { Authorization: `Bearer ${token}` }
      //   });
      //   toast.success("Roz Namacha fields saved to server.");
      // } catch (error) {
      //   console.warn("Backend roznamacha save failed, using localStorage only:", error);
      // }
    } catch (error) {
      console.error("Save roznamacha template error:", error);
      toast.error("Failed to save: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Roz Namacha Fields - Admin Panel</title></Helmet>
      <div className="min-h-screen bg-brand-background p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-brand-card/60 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-brand-header">Roz Namacha Field Management</h1>
              <p className="text-gray-600 mt-1 text-sm md:text-base">
                Define columns for Supplier Bills, Customer Receipts, Recovery Data, and Expenses roznamcha for each company.
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <button
                onClick={() => navigate("/admin")}
                className="bg-white border border-brand-header text-brand-header hover:bg-brand-header hover:text-brand-background font-semibold px-4 py-2 rounded-lg transition text-sm"
              >
                Back to Admin
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={isLoading || !selectedCompany}
                className="bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-4 py-2 rounded-lg transition text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? "Saving..." : "Save Fields"}
              </button>
            </div>
          </div>

          {/* Company Selector */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-brand-card/40">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Select Company</h2>
            <select
              value={selectedCompany?._id || ""}
              onChange={(e) => handleCompanySelect(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-button"
            >
              <option value="">-- Select Company --</option>
              {companies.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.shopName || company.username} ({company.email})
                </option>
              ))}
            </select>
            {selectedCompany && (
              <p className="mt-2 text-sm text-gray-600">
                Editing fields for: <span className="font-semibold">{selectedCompany.shopName || selectedCompany.username}</span>
              </p>
            )}
          </div>

          {/* Columns Editor */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Supplier Columns */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-brand-card/40">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Supplier Roz Namacha Columns</h3>
                <button
                  onClick={() => handleAddColumn("supplier")}
                  className="text-sm bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-3 py-1 rounded-lg transition"
                >
                  + Add Column
                </button>
              </div>
              <div className="space-y-3">
                {supplierColumns.map((col, index) => {
                  const fieldKey = `supplier_${index}`;
                  const isEditing = editingField === fieldKey;
                  return (
                    <div 
                      key={index} 
                      className={`border rounded-lg p-3 flex flex-col gap-2 transition ${
                        isEditing 
                          ? 'border-brand-button shadow-md bg-yellow-50' 
                          : 'border-brand-card/60'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold text-gray-700">
                          {col.labelEn || col.labelUr || `Field ${index + 1}`}
                          {col.isDefault && <span className="ml-2 text-xs text-gray-500">(Default)</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleEditField("supplier", index)}
                          className={`text-xs font-semibold px-2 py-1 rounded transition ${
                            isEditing
                              ? 'bg-brand-header text-white hover:bg-emerald-800'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {isEditing ? 'Done' : 'Edit'}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Key (English ID)</label>
                          <input
                            type="text"
                            value={col.key}
                            onChange={(e) => handleUpdateColumn("supplier", index, "key", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="e.g. sale, cash"
                            disabled={col.isDefault || !isEditing}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                          <select
                            value={col.type}
                            onChange={(e) => handleUpdateColumn("supplier", index, "type", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            disabled={!isEditing}
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label (English)</label>
                          <input
                            type="text"
                            value={col.labelEn}
                            onChange={(e) => handleUpdateColumn("supplier", index, "labelEn", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Sale"
                            disabled={!isEditing}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label (Urdu)</label>
                          <input
                            type="text"
                            value={col.labelUr}
                            onChange={(e) => handleUpdateColumn("supplier", index, "labelUr", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="سیل"
                            disabled={!isEditing}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-1">
                        <button
                          type="button"
                          onClick={() => handleInsertColumn("supplier", index)}
                          className="text-xs text-brand-header hover:text-emerald-800 font-medium"
                          disabled={!isEditing}
                        >
                          + Add below
                        </button>
                        <div className="flex gap-2">
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleSaveSingleField("supplier", index)}
                              className="text-xs bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-2 py-1 rounded transition"
                            >
                              Save
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveColumn("supplier", index)}
                            className="text-xs bg-brand-alert hover:bg-red-700 text-white font-semibold px-2 py-1 rounded transition"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Customer Columns */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-brand-card/40">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Customer Roz Namacha Columns</h3>
                <button
                  onClick={() => handleAddColumn("customer")}
                  className="text-sm bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-3 py-1 rounded-lg transition"
                >
                  + Add Column
                </button>
              </div>
              <div className="space-y-3">
                {customerColumns.map((col, index) => {
                  const fieldKey = `customer_${index}`;
                  const isEditing = editingField === fieldKey;
                  return (
                    <div 
                      key={index} 
                      className={`border rounded-lg p-3 flex flex-col gap-2 transition ${
                        isEditing 
                          ? 'border-brand-button shadow-md bg-yellow-50' 
                          : 'border-brand-card/60'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold text-gray-700">
                          {col.labelEn || col.labelUr || `Field ${index + 1}`}
                          {col.isDefault && <span className="ml-2 text-xs text-gray-500">(Default)</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleEditField("customer", index)}
                          className={`text-xs font-semibold px-2 py-1 rounded transition ${
                            isEditing
                              ? 'bg-brand-header text-white hover:bg-emerald-800'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {isEditing ? 'Done' : 'Edit'}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Key (English ID)</label>
                          <input
                            type="text"
                            value={col.key}
                            onChange={(e) => handleUpdateColumn("customer", index, "key", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="e.g. sale, cash"
                            disabled={col.isDefault || !isEditing}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                          <select
                            value={col.type}
                            onChange={(e) => handleUpdateColumn("customer", index, "type", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            disabled={!isEditing}
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label (English)</label>
                          <input
                            type="text"
                            value={col.labelEn}
                            onChange={(e) => handleUpdateColumn("customer", index, "labelEn", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Sale"
                            disabled={!isEditing}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label (Urdu)</label>
                          <input
                            type="text"
                            value={col.labelUr}
                            onChange={(e) => handleUpdateColumn("customer", index, "labelUr", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="سیل"
                            disabled={!isEditing}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-1">
                        <button
                          type="button"
                          onClick={() => handleInsertColumn("customer", index)}
                          className="text-xs text-brand-header hover:text-emerald-800 font-medium"
                          disabled={!isEditing}
                        >
                          + Add below
                        </button>
                        <div className="flex gap-2">
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleSaveSingleField("customer", index)}
                              className="text-xs bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-2 py-1 rounded transition"
                            >
                              Save
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveColumn("customer", index)}
                            className="text-xs bg-brand-alert hover:bg-red-700 text-white font-semibold px-2 py-1 rounded transition"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recovery Data & Expenses Columns */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Recovery Data Columns */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-brand-card/40">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Recovery Data Roz Namacha Columns</h3>
                <button
                  onClick={() => handleAddColumn("recovery")}
                  className="text-sm bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-3 py-1 rounded-lg transition"
                >
                  + Add Column
                </button>
              </div>
              <div className="space-y-3">
                {recoveryColumns.map((col, index) => {
                  const fieldKey = `recovery_${index}`;
                  const isEditing = editingField === fieldKey;
                  return (
                    <div 
                      key={index} 
                      className={`border rounded-lg p-3 flex flex-col gap-2 transition ${
                        isEditing 
                          ? 'border-brand-button shadow-md bg-yellow-50' 
                          : 'border-brand-card/60'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold text-gray-700">
                          {col.labelEn || col.labelUr || `Field ${index + 1}`}
                          {col.isDefault && <span className="ml-2 text-xs text-gray-500">(Default)</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleEditField("recovery", index)}
                          className={`text-xs font-semibold px-2 py-1 rounded transition ${
                            isEditing
                              ? 'bg-brand-header text-white hover:bg-emerald-800'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {isEditing ? 'Done' : 'Edit'}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Key (English ID)</label>
                          <input
                            type="text"
                            value={col.key}
                            onChange={(e) => handleUpdateColumn("recovery", index, "key", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="e.g. recoveryNumber, partyName"
                            disabled={col.isDefault || !isEditing}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                          <select
                            value={col.type}
                            onChange={(e) => handleUpdateColumn("recovery", index, "type", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            disabled={!isEditing}
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label (English)</label>
                          <input
                            type="text"
                            value={col.labelEn}
                            onChange={(e) => handleUpdateColumn("recovery", index, "labelEn", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Recovery Number"
                            disabled={!isEditing}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label (Urdu)</label>
                          <input
                            type="text"
                            value={col.labelUr}
                            onChange={(e) => handleUpdateColumn("recovery", index, "labelUr", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="ریکوری نمبر"
                            disabled={!isEditing}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-1">
                        <button
                          type="button"
                          onClick={() => handleInsertColumn("recovery", index)}
                          className="text-xs text-brand-header hover:text-emerald-800 font-medium"
                          disabled={!isEditing}
                        >
                          + Add below
                        </button>
                        <div className="flex gap-2">
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleSaveSingleField("recovery", index)}
                              className="text-xs bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-2 py-1 rounded transition"
                            >
                              Save
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveColumn("recovery", index)}
                            className="text-xs bg-brand-alert hover:bg-red-700 text-white font-semibold px-2 py-1 rounded transition"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Expense Columns */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-brand-card/40">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Expenses Roz Namacha Columns</h3>
                <button
                  onClick={() => handleAddColumn("expense")}
                  className="text-sm bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-3 py-1 rounded-lg transition"
                >
                  + Add Column
                </button>
              </div>
              <div className="space-y-3">
                {expenseColumns.map((col, index) => {
                  const fieldKey = `expense_${index}`;
                  const isEditing = editingField === fieldKey;
                  return (
                    <div 
                      key={index} 
                      className={`border rounded-lg p-3 flex flex-col gap-2 transition ${
                        isEditing 
                          ? 'border-brand-button shadow-md bg-yellow-50' 
                          : 'border-brand-card/60'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold text-gray-700">
                          {col.labelEn || col.labelUr || `Field ${index + 1}`}
                          {col.isDefault && <span className="ml-2 text-xs text-gray-500">(Default)</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleEditField("expense", index)}
                          className={`text-xs font-semibold px-2 py-1 rounded transition ${
                            isEditing
                              ? 'bg-brand-header text-white hover:bg-emerald-800'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {isEditing ? 'Done' : 'Edit'}
                        </button>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Key (English ID)</label>
                          <input
                            type="text"
                            value={col.key}
                            onChange={(e) => handleUpdateColumn("expense", index, "key", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="e.g. expenseNumber, category"
                            disabled={col.isDefault || !isEditing}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                          <select
                            value={col.type}
                            onChange={(e) => handleUpdateColumn("expense", index, "type", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            disabled={!isEditing}
                          >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label (English)</label>
                          <input
                            type="text"
                            value={col.labelEn}
                            onChange={(e) => handleUpdateColumn("expense", index, "labelEn", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Expense Number"
                            disabled={!isEditing}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Label (Urdu)</label>
                          <input
                            type="text"
                            value={col.labelUr}
                            onChange={(e) => handleUpdateColumn("expense", index, "labelUr", e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="خرچ نمبر"
                            disabled={!isEditing}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-1">
                        <button
                          type="button"
                          onClick={() => handleInsertColumn("expense", index)}
                          className="text-xs text-brand-header hover:text-emerald-800 font-medium"
                          disabled={!isEditing}
                        >
                          + Add below
                        </button>
                        <div className="flex gap-2">
                          {isEditing && (
                            <button
                              type="button"
                              onClick={() => handleSaveSingleField("expense", index)}
                              className="text-xs bg-brand-button hover:bg-yellow-300 text-gray-900 font-semibold px-2 py-1 rounded transition"
                            >
                              Save
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveColumn("expense", index)}
                            className="text-xs bg-brand-alert hover:bg-red-700 text-white font-semibold px-2 py-1 rounded transition"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminRozNamachaFields;


